import { createContext, FC, ReactNode, useContext, useState } from "react";
import { IntlProvider } from 'react-intl';
import * as translations from 'translations/index';

export const LOCALES = {
  CS: "cs",
  CS_CZ: "cs-CZ",
  DE: "de",
  DE_DE: "de-DE",
  EN: "en",
  EN_GB: "en-GB",
  EN_US: "en-US",
  ES: "es",
  ES_AR: "es-AR",
  ES_CL: "es-CL",
  ES_CO: "es-CO",
  ES_EC: "es-EC",
  ES_ES: "es-ES",
  ES_PE: "es-PE",
  FR: "fr",
  FR_BE: "fr-BE",
  FR_CA: "fr-CA",
  FR_FR: "fr-FR",
  KO: "ko",
  KO_KR: "ko-KR",
  NL: "nl",
  NL_BE: "nl-BE",
  NL_NL: "nl-NL",
  PL: "pl",
  PL_PL: "pl-PL",
  PT: "pt",
  PT_BR: "pt-BR",
  PT_PT: "pt-PT",
  SK: "sk",
  SK_SK: "sk-SK",
  ZH: "zh",
  ZH_CHS: "zh-CHS",
  ZH_CHT: "zh-CHT",
  ZH_CN: "zh-CN",
  ZH_HANS: "zh-HANS",
  ZH_HANT: "zh-HANT",
  ZH_TW: "zh-TW",
};

const supportedMessages = {
  [LOCALES.CS]: translations.cs,
  [LOCALES.CS_CZ]: translations.cs_CZ,
  [LOCALES.DE]: translations.de,
  [LOCALES.DE_DE]: translations.de_DE,
  [LOCALES.EN]: translations.en,
  [LOCALES.EN_GB]: translations.en_GB,
  [LOCALES.EN_US]: translations.en_US,
  [LOCALES.ES]: translations.es,
  [LOCALES.ES_AR]: translations.es_AR,
  [LOCALES.ES_CL]: translations.es_CL,
  [LOCALES.ES_CO]: translations.es_CO,
  [LOCALES.ES_EC]: translations.es_EC,
  [LOCALES.ES_ES]: translations.es_ES,
  [LOCALES.ES_PE]: translations.es_PE,
  [LOCALES.FR]: translations.fr,
  [LOCALES.FR_BE]: translations.fr_BE,
  [LOCALES.FR_CA]: translations.fr_CA,
  [LOCALES.FR_FR]: translations.fr_FR,
  [LOCALES.KO]: translations.ko,
  [LOCALES.KO_KR]: translations.ko_KR,
  [LOCALES.NL]: translations.nl,
  [LOCALES.NL_BE]: translations.nl_BE,
  [LOCALES.NL_NL]: translations.nl_NL,
  [LOCALES.PL]: translations.pl,
  [LOCALES.PL_PL]: translations.pl_PL,
  [LOCALES.PT]: translations.pt,
  [LOCALES.PT_BR]: translations.pt_BR,
  [LOCALES.PT_PT]: translations.pt_PT,
  [LOCALES.SK]: translations.sk,
  [LOCALES.SK_SK]: translations.sk_SK,
  [LOCALES.ZH]: translations.zh,
  [LOCALES.ZH_CHS]: translations.zh_CHS,
  [LOCALES.ZH_CHT]: translations.zh_CHT,
  [LOCALES.ZH_CN]: translations.zh_CN,
  [LOCALES.ZH_HANS]: translations.zh_Hans,
  [LOCALES.ZH_HANT]: translations.zh_Hant,
  [LOCALES.ZH_TW]: translations.zh_TW
};

export type TranslationContextType = {
  locale: string;
  messages: any;
  setCurrentLocale: (locale: string) => void;
};

const TranslationContextDefaultState: TranslationContextType = {
  locale: LOCALES.EN_US,
  messages: supportedMessages[LOCALES.EN_US],
  setCurrentLocale: (locale: string) => { }
};

export const TranslationContext = createContext(TranslationContextDefaultState);

interface TranslationProviderProps {
  children: ReactNode;
}

const TranslationProvider: FC<TranslationProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState(TranslationContextDefaultState.locale);
  const [messages, setMessages] = useState(TranslationContextDefaultState.messages);

  const setCurrentLocale = (locale: string) => {
    setLocale(locale);
    setMessages(supportedMessages[locale]);
  };

  return <TranslationContext.Provider value={{ locale, messages, setCurrentLocale }}>
    <IntlProvider messages={messages} locale={locale} defaultLocale={TranslationContextDefaultState.locale}>
      {children}
    </IntlProvider>
  </TranslationContext.Provider>
};

export default TranslationProvider;
export const useTranslations = () => useContext(TranslationContext);